/**
 * Readdy 호스팅 이미지의 자체 제공(self-host) 레지스트리 (이식성 M35).
 *
 * 화면이 쓰던 세 호스트(static.readdy.ai · public.readdy.ai · storage.readdy-site.link)의
 * 이미지 중 **사용자 사진이 아닌 것**(브랜드 로고 3종 + 홈 히어로 슬라이드 10장)을
 * 저장소 `public/assets/` 로 옮기기 위한 단일 출처다.
 *
 * - `ownAssetLocal(key)` = 앱 오리진 기준 로컬 정적 경로(`<BASE_URL>/assets/<file>`).
 *   Vite 가 `public/` 을 그대로 복사해 사이트 루트에서 서빙하므로 단순 정적 경로다.
 * - `ownAssetRemote(key)` = 기존 레디 호스팅 원본 URL(**폴백 전용**).
 *
 * 파일은 이 환경에서 바이너리를 직접 커밋할 수 없어
 * `node scripts/fetch-own-assets.mjs` 로 채운다(1430 PDF 글꼴과 동일 패턴).
 * 파일이 아직 없으면 로컬 요청이 실패하고, 호출부가 `ownAssetRemote(...)` 로
 * 폴백하므로 **배치 전에도 화면이 그대로** 동작한다.
 */

export interface OwnAsset {
  /** public/assets/ 안에 놓일 파일명. */
  file: string;
  /** 사람이 읽는 용도 메모(크기는 스크립트 실행 후 실제 파일로 확정). */
  label: string;
}

export const OWN_ASSETS = {
  logoCombined: {
    file: 'logo-elkaway-combined.png',
    label: 'Elkaway combined logo (elk + elephant)',
  },
  logoElephant: {
    file: 'logo-elkaway-elephant.png',
    label: 'Elkaway elephant logo (trip/dashboard)',
  },
  logoElk: {
    file: 'logo-elkaway-elk.png',
    label: 'Elkaway elk logo (travel board / favicon)',
  },
  hero1: {
    file: 'hero-01.png',
    label: 'Home hero slide 1',
  },
  hero2: {
    file: 'hero-02.png',
    label: 'Home hero slide 2',
  },
  hero3: {
    file: 'hero-03.png',
    label: 'Home hero slide 3',
  },
  hero4: {
    file: 'hero-04.png',
    label: 'Home hero slide 4',
  },
  hero5: {
    file: 'hero-05.png',
    label: 'Home hero slide 5',
  },
  hero6: {
    file: 'hero-06.png',
    label: 'Home hero slide 6',
  },
  hero7: {
    file: 'hero-07.png',
    label: 'Home hero slide 7',
  },
  hero8: {
    file: 'hero-08.png',
    label: 'Home hero slide 8',
  },
  hero9: {
    file: 'hero-09.png',
    label: 'Home hero slide 9',
  },
  hero10: {
    file: 'hero-10.png',
    label: 'Home hero slide 10',
  },
} as const;

export type OwnAssetKey = keyof typeof OWN_ASSETS;

/** 앱 오리진 기준 정적 경로. 배포 base(`import.meta.env.BASE_URL`)를 반영한다. */
export function ownAssetUrl(file: string): string {
  const base = (import.meta.env.BASE_URL || '/').replace(/\/?$/, '/');
  return `${base}assets/${file}`;
}

/** 자체 제공(로컬) 경로 — 화면이 우선 쓰는 값. */
export function ownAssetLocal(key: OwnAssetKey): string {
  return ownAssetUrl(OWN_ASSETS[key].file);
}
